import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";

interface CallNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callId: string;
  onSuccess: () => void;
}

interface CallNote {
  id: string;
  note: string;
  note_type: string;
  created_at: string;
  created_by: string;
  profiles: {
    display_name: string;
  };
}

export function CallNotesDialog({ open, onOpenChange, callId, onSuccess }: CallNotesDialogProps) {
  const { user } = useAuth();
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [notes, setNotes] = useState<CallNote[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadNotes();
    }
  }, [open, callId]);

  const loadNotes = async () => {
    const { data, error } = await supabase
      .from('call_notes')
      .select(`
        *,
        profiles:created_by (display_name)
      `)
      .eq('call_id', callId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading notes:', error);
    } else {
      setNotes(data || []);
    }
  };

  const handleSubmit = async () => {
    if (!note.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setLoading(true);
    const { error } = await supabase
      .from('call_notes')
      .insert({
        call_id: callId,
        note: note.trim(),
        note_type: noteType,
        created_by: user?.id
      });

    if (error) {
      toast.error("Failed to add note");
      console.error('Error adding note:', error);
    } else {
      toast.success("Note added successfully");
      setNote("");
      setNoteType("general");
      loadNotes();
      onSuccess();
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Call Notes</DialogTitle>
          <DialogDescription>
            Add notes and track conversation details for this call
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="note-type">Note Type</Label>
            <Select value={noteType} onValueChange={setNoteType}>
              <SelectTrigger id="note-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="follow_up">Follow-up Required</SelectItem>
                <SelectItem value="callback">Callback Scheduled</SelectItem>
                <SelectItem value="quote">Quote Details</SelectItem>
                <SelectItem value="objection">Objection/Concern</SelectItem>
                <SelectItem value="decision">Decision Made</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="note">Note</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter your note here..."
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Adding..." : "Add Note"}
          </Button>
        </DialogFooter>

        {notes.length > 0 && (
          <div className="mt-6 space-y-4">
            <h4 className="font-semibold">Previous Notes</h4>
            {notes.map((note) => (
              <div key={note.id} className="border rounded-lg p-3 bg-muted/50">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase">
                    {note.note_type.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
                <p className="text-sm">{note.note}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  By: {note.profiles?.display_name || 'Unknown'}
                </p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}